import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import type { MultiFactorResolver, UserCredential } from 'firebase/auth'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { resolveTotpSignIn } from '../lib/totpMfa'

interface Props {
  /** Firebase `auth/multi-factor-auth-required` から得た TOTP resolver. */
  resolver: MultiFactorResolver | null
  /** 解決成功 → 呼び元が finishFirebaseLogin に合流。 */
  onResolved: (cred: UserCredential) => void
  /** ユーザーがログインを中断。 */
  onCancel: () => void
}

/**
 * TOTP 第2要素 sign-in モーダル (#815 / #789 decision).
 *
 * TOTP enroll 済ユーザーは次回ログインで Firebase が
 * `auth/multi-factor-auth-required` を投げる。`Login.tsx` がそれを intercept
 * し、本 modal で 6 桁コードを取り `resolver.resolveSignIn` で解決する。
 * 解決しないとログイン継続不能なので「これを通さないと先に進めない」
 * 半 block だが、enrollment と違い**中断 (cancel) は許可**する
 * (= 別アカウントで入り直す導線を塞がない)。
 */
export default function TotpSignInModal({ resolver, onResolved, onCancel }: Props) {
  const { t } = useI18n()
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!resolver) return null

  const handleVerify = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      const cred = await resolveTotpSignIn(resolver, code.trim())
      onResolved(cred)
    } catch (e) {
      const code2 = (e as { code?: string })?.code
      console.error('[TotpSignIn] resolve failed:', e)
      if (code2 === 'auth/invalid-verification-code' || code2 === 'auth/invalid-code') {
        showToast({ type: 'error', message: t('totp.invalidCode') })
      } else {
        showToast({ type: 'error', message: t('totp.signInFailed') })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (submitting) return
    showToast({ type: 'info', message: t('totp.cancelled') })
    onCancel()
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-yokoso-red/10">
          <ShieldCheck size={32} className="text-yokoso-red" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('totp.signInTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t('totp.signInDescription')}
        </p>

        <label className="block text-xs font-body text-text-secondary mb-1">
          {t('totp.codeInputLabel')}
        </label>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && code.length === 6 && !submitting) handleVerify()
          }}
          className="w-full h-12 mb-4 text-center tracking-[0.4em] text-lg font-mono border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
          placeholder="000000"
        />
        <button
          onClick={handleVerify}
          disabled={submitting || code.length !== 6}
          className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
            submitting || code.length !== 6
              ? 'bg-gray-300 text-white cursor-not-allowed'
              : 'bg-yokoso-red text-white tap-effect'
          }`}
        >
          {submitting ? t('totp.enrolling') : t('totp.signInAction')}
        </button>
        <button
          onClick={handleCancel}
          disabled={submitting}
          className="w-full h-11 mt-2 rounded-xl font-body text-sm text-text-secondary transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          {t('totp.cancelAction')}
        </button>

        <p className="font-body text-[11px] text-text-secondary text-center mt-3 leading-relaxed">
          {t('totp.recoveryContact')}
        </p>
      </div>
    </div>
  )
}
